import type {
  ExpenseItemWithRelations,
  ExpenseStatus,
  PaymentMethod,
  CatalogItem,
  CreateExpenseItemInput,
  UpdateExpenseItemInput,
  CreateExpensePaymentInput,
  ExpensePayment,
  CreateCatalogItemInput,
  UpdateCatalogItemInput,
} from '@peles/shared';
import { apiClient } from './client';

export interface ExpenseConfig {
  statuses: ExpenseStatus[];
  paymentMethods: PaymentMethod[];
  catalogItems: CatalogItem[];
}

export async function getExpenseConfig(): Promise<ExpenseConfig> {
  const { data } = await apiClient.get<ExpenseConfig>('/expenses/config');
  return data;
}

export async function getExpenseItems(filters?: {
  eventId?: string;
  vendorId?: string;
}): Promise<ExpenseItemWithRelations[]> {
  const params: Record<string, string> = {};
  if (filters?.eventId) params.eventId = filters.eventId;
  if (filters?.vendorId) params.vendorId = filters.vendorId;
  const { data } = await apiClient.get<ExpenseItemWithRelations[]>('/expenses', { params });
  return data;
}

export async function getExpenseItem(id: string): Promise<ExpenseItemWithRelations> {
  const { data } = await apiClient.get<ExpenseItemWithRelations>(`/expenses/${id}`);
  return data;
}

export async function createExpenseItem(
  input: CreateExpenseItemInput,
): Promise<ExpenseItemWithRelations> {
  const { data } = await apiClient.post<ExpenseItemWithRelations>('/expenses', input);
  return data;
}

export async function updateExpenseItem(
  id: string,
  updates: UpdateExpenseItemInput,
): Promise<ExpenseItemWithRelations> {
  const { data } = await apiClient.patch<ExpenseItemWithRelations>(`/expenses/${id}`, updates);
  return data;
}

export async function deleteExpenseItem(id: string): Promise<void> {
  await apiClient.delete(`/expenses/${id}`);
}

export async function addExpensePayment(
  expenseItemId: string,
  input: Omit<CreateExpensePaymentInput, 'expenseItemId'>,
): Promise<ExpensePayment> {
  const { data } = await apiClient.post<ExpensePayment>(
    `/expenses/${expenseItemId}/payments`,
    input,
  );
  return data;
}

export async function removeExpensePayment(
  expenseItemId: string,
  paymentId: string,
): Promise<void> {
  await apiClient.delete(`/expenses/${expenseItemId}/payments/${paymentId}`);
}

export async function createCatalogItem(input: CreateCatalogItemInput): Promise<CatalogItem> {
  const { data } = await apiClient.post<CatalogItem>('/expenses/catalog-items', input);
  return data;
}

export async function updateCatalogItem(
  id: string,
  updates: UpdateCatalogItemInput,
): Promise<CatalogItem> {
  const { data } = await apiClient.patch<CatalogItem>(`/expenses/catalog-items/${id}`, updates);
  return data;
}
